/**
 * compileSignatureHtml — deterministic signature spec -> Gmail-safe HTML.
 *
 * Every Gmail/email-client restriction is enforced by construction (see
 * docs/gmail-signature-guide.md):
 *   - nested presentation tables, no divs for layout
 *   - inline styles only (no <style>, no classes), px units only
 *   - spacing via td padding / padded wrapper divs, never margin
 *   - images: explicit width/height attributes + inline CSS + display:block
 *   - mso-line-height-rule before every line-height (classic Outlook)
 *   - tel:/mailto:/https links only, always inline-colored
 *   - all text HTML-escaped UTF-8
 *
 * Output is compact (no pretty-printing) because Gmail's 10,000-character
 * limit counts the raw HTML.
 */

import { FONT_BY_ID, WEB_SAFE_FONTS, SIGNATURE_CHAR_LIMIT } from './signatureSpec';

function esc(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escAttr(value) {
  return esc(value);
}

function fontStack(spec) {
  const font = FONT_BY_ID[spec.global.fontId] || WEB_SAFE_FONTS[0];
  return font.stack;
}

const SAFE_HREF_RE = /^(https:\/\/|http:\/\/|mailto:|tel:)/i;

function safeHref(href) {
  const raw = String(href || '').trim();
  return SAFE_HREF_RE.test(raw) ? raw : '';
}

/**
 * Wrap block html in its spacing/alignment container. When ctx.annotate is
 * set (preview only — never for export), every block gets a data-sig-block
 * attribute so the Studio can offer click-to-select and glow highlights.
 */
function wrapBlock(inner, { spacingTop = 0, align = null, id } = {}, ctx = {}) {
  const pad = spacingTop > 0 ? `padding-top:${spacingTop}px;` : '';
  const alignAttr = align && align !== 'left' ? ` align="${align}"` : '';
  const annotate = ctx.annotate && id ? ` data-sig-block="${escAttr(id)}"` : '';
  if (!pad && !alignAttr && !annotate) return inner;
  return `<div${annotate}${alignAttr}${pad ? ` style="${pad}"` : ''}>${inner}</div>`;
}

function textDivStyle(ctx, { size, lineHeight, color, bold, italic, letterSpacing }) {
  const fs = size || ctx.fontSize;
  const lh = lineHeight || fs + 5;
  return (
    `mso-line-height-rule:exactly;font-family:${ctx.font};font-size:${fs}px;line-height:${lh}px;` +
    `color:${color || ctx.textColor};` +
    (bold ? 'font-weight:bold;' : '') +
    (italic ? 'font-style:italic;' : '') +
    (letterSpacing ? `letter-spacing:${letterSpacing}px;` : '')
  );
}

function renderImage(block, ctx) {
  const src = String(block.src || '').trim();
  if (!/^https:\/\//i.test(src)) return '';
  // null width/height = natural size (imported signatures without explicit
  // dimensions): omit that dimension entirely so clients render it as the
  // original HTML did. When only one dimension exists browsers scale
  // proportionally — same as the source markup.
  const hasW = block.width != null && Number.isFinite(Number(block.width));
  const hasH = block.height != null && Number.isFinite(Number(block.height));
  const w = hasW ? Math.min(Math.max(1, Math.round(block.width)), ctx.maxWidth) : null;
  const h = hasH ? Math.max(1, Math.round(block.height)) : null;
  const radius = block.circle ? 'border-radius:50%;' : '';
  const dimAttrs = `${w != null ? ` width="${w}"` : ''}${h != null ? ` height="${h}"` : ''}`;
  // width in CSS too (Outlook 120-DPI scaling); the height attr alone is
  // respected everywhere, so no height style — keeps the output lean.
  const dimStyles = w != null ? `width:${w}px;` : '';
  const altAttr = block.alt ? ` alt="${escAttr(block.alt)}"` : '';
  let img =
    `<img src="${escAttr(src)}"${dimAttrs}${altAttr} ` +
    `style="display:block;border:0;${dimStyles}${radius}" />`;
  const href = safeHref(block.href);
  if (href) img = `<a href="${escAttr(href)}" target="_blank" style="text-decoration:none;">${img}</a>`;
  return wrapBlock(img, block, ctx);
}

/** One rich inline run within a text block (bold/italic/color/size/link/inline icon). */
function renderSegment(seg, ctx) {
  const padStyle = seg.paddingRight ? `padding-right:${seg.paddingRight}px;` : '';

  // Inline image segment — an icon flowing with the text on the same line.
  if (seg.image) {
    const src = String(seg.image.src || '').trim();
    if (!/^https:\/\//i.test(src)) return '';
    const hasW = seg.image.width != null && Number.isFinite(Number(seg.image.width));
    const hasH = seg.image.height != null && Number.isFinite(Number(seg.image.height));
    const w = hasW ? Math.max(1, Math.round(seg.image.width)) : null;
    const h = hasH ? Math.max(1, Math.round(seg.image.height)) : null;
    const dimAttrs = `${w != null ? ` width="${w}"` : ''}${h != null ? ` height="${h}"` : ''}`;
    const altAttr = seg.image.alt ? ` alt="${escAttr(seg.image.alt)}"` : '';
    const img =
      `<img src="${escAttr(src)}"${dimAttrs}${altAttr} ` +
      `style="display:inline-block;vertical-align:middle;border:0;${padStyle}" />`;
    const href = safeHref(seg.image.href);
    return href
      ? `<a href="${escAttr(href)}" target="_blank" style="text-decoration:none;">${img}</a>`
      : img;
  }

  const content = esc(seg.text).replace(/\n/g, '<br />');
  const runStyles =
    (seg.size ? `font-size:${seg.size}px;` : '') +
    (seg.bold ? 'font-weight:bold;' : '') +
    (seg.italic ? 'font-style:italic;' : '') +
    (seg.letterSpacing ? `letter-spacing:${seg.letterSpacing}px;` : '');
  const href = safeHref(seg.href);
  if (href) {
    const deco = seg.underline ? 'underline' : 'none';
    return `<a href="${escAttr(href)}" target="_blank" style="color:${seg.color || ctx.linkColor};text-decoration:${deco};${runStyles}${padStyle}">${content}</a>`;
  }
  const colorStyle = seg.color ? `color:${seg.color};` : '';
  const decoStyle = seg.underline ? 'text-decoration:underline;' : '';
  const style = colorStyle + runStyles + decoStyle + padStyle;
  return style ? `<span style="${style}">${content}</span>` : content;
}

function textSegments(block) {
  return Array.isArray(block.segments)
    ? block.segments.filter((s) => s && (s.image || String(s.text || '').length))
    : [];
}

function renderText(block, ctx) {
  const segments = textSegments(block);
  if (!segments.length && !String(block.text || '').trim()) return '';
  if (segments.length) {
    // Segments carry their own bold/italic — the wrapping div only sets the
    // line's font/size/line-height/base color so runs compose predictably.
    const style = textDivStyle(ctx, { size: block.size, lineHeight: block.lineHeight, color: block.color, letterSpacing: block.letterSpacing });
    const inner = segments.map((s) => renderSegment(s, ctx)).join('');
    return wrapBlock(`<div style="${style}">${inner}</div>`, block, ctx);
  }
  const style = textDivStyle(ctx, block);
  const content = esc(block.text).replace(/\n/g, '<br />');
  const href = safeHref(block.href);
  const deco = block.underline ? 'underline' : 'none';
  const inner = href
    ? `<a href="${escAttr(href)}" target="_blank" style="color:${block.color || ctx.linkColor};text-decoration:${deco};">${content}</a>`
    : (block.underline ? `<span style="text-decoration:underline;">${content}</span>` : content);
  return wrapBlock(`<div style="${style}">${inner}</div>`, block, ctx);
}

function contactHref(item) {
  if (item.kind === 'phone') {
    const digits = String(item.value || '').replace(/[^\d+]/g, '');
    if (!digits) return '';
    return `tel:${digits.startsWith('+') ? digits : `+1${digits}`}`;
  }
  if (item.kind === 'email') return item.value ? `mailto:${item.value}` : '';
  if (item.kind === 'website' || item.kind === 'link') {
    const v = String(item.value || '').trim();
    if (!v) return '';
    return safeHref(/^(https?:\/\/|mailto:|tel:)/i.test(v) ? v : `https://${v}`);
  }
  return ''; // kind 'text' renders as plain text
}

function renderContact(block, ctx) {
  const items = (block.items || []).filter((i) => i.label);
  if (!items.length) return '';
  const size = block.size || ctx.fontSize;
  const style = textDivStyle(ctx, { size, color: block.color || ctx.textColor });
  const parts = items.map((item) => {
    const href = contactHref(item);
    return href
      ? `<a href="${escAttr(href)}" target="_blank" style="color:${ctx.linkColor};text-decoration:none;">${esc(item.label)}</a>`
      : `<span style="color:${block.color || ctx.textColor};">${esc(item.label)}</span>`;
  });
  if (block.layout === 'stacked') {
    const rows = parts.map((p, i) => (i === 0 ? `<div style="${style}">${p}</div>` : `<div style="${style}padding-top:2px;">${p}</div>`)).join('');
    return wrapBlock(rows, block, ctx);
  }
  const sep = `<span style="color:#999999;">${esc(block.separator || ' | ')}</span>`;
  return wrapBlock(`<div style="${style}">${parts.join(sep)}</div>`, block, ctx);
}

function renderSocial(block, ctx) {
  const icons = (block.icons || []).filter((i) => safeHref(i.href));
  if (!icons.length) return '';
  const size = Math.round(block.size) || 20;
  const gap = Math.round(block.gap) || 8;
  const cells = icons.map((icon, i) => {
    const pad = i < icons.length - 1 ? `padding:0 ${gap}px 0 0;` : 'padding:0;';
    const href = escAttr(safeHref(icon.href));
    if (icon.iconUrl && /^https:\/\//i.test(icon.iconUrl)) {
      return (
        `<td style="${pad}"><a href="${href}" target="_blank" style="text-decoration:none;border:0;">` +
        `<img src="${escAttr(icon.iconUrl)}" width="${size}" height="${size}" alt="${escAttr(icon.label || icon.network)}" ` +
        `style="display:block;border:0;width:${size}px;height:${size}px;" /></a></td>`
      );
    }
    const style = `mso-line-height-rule:exactly;font-family:${ctx.font};font-size:${ctx.fontSize}px;line-height:${ctx.fontSize + 5}px;color:${ctx.linkColor};font-weight:bold;text-decoration:none;`;
    return `<td style="${pad}"><a href="${href}" target="_blank" style="${style}">${esc(icon.label || icon.network)}</a></td>`;
  }).join('');
  const table = `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;border:0;"><tr>${cells}</tr></table>`;
  return wrapBlock(table, block, ctx);
}

function renderButton(block, ctx) {
  const href = safeHref(block.href);
  if (!href || !String(block.text || '').trim()) return '';
  const bg = block.bg || ctx.accentColor;
  const color = block.color || '#ffffff';
  const size = block.size || ctx.fontSize;
  const table =
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:separate;border:0;"><tr>` +
    `<td style="background-color:${bg};border-radius:${block.radius || 0}px;padding:${block.paddingY}px ${block.paddingX}px;">` +
    `<a href="${escAttr(href)}" target="_blank" style="mso-line-height-rule:exactly;font-family:${ctx.font};font-size:${size}px;line-height:${size + 3}px;font-weight:bold;color:${color};text-decoration:none;display:inline-block;">${esc(block.text)}</a>` +
    `</td></tr></table>`;
  return wrapBlock(table, block, ctx);
}

function renderDivider(block, ctx) {
  const line =
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;"><tr>` +
    `<td style="border-top:${block.thickness || 1}px solid ${block.color || '#dddddd'};font-size:0;line-height:0;">&nbsp;</td></tr></table>`;
  const bottom = block.spacingBottom > 0 ? `<div style="height:${block.spacingBottom}px;line-height:${block.spacingBottom}px;font-size:1px;">&nbsp;</div>` : '';
  return wrapBlock(line, block, ctx) + bottom;
}

function renderSpacer(block, ctx) {
  const h = Math.round(block.height) || 8;
  return wrapBlock(`<div style="height:${h}px;line-height:${h}px;font-size:1px;">&nbsp;</div>`, { id: block.id }, ctx);
}

function renderDisclaimer(block, ctx) {
  if (!String(block.text || '').trim()) return '';
  const style = textDivStyle(ctx, { size: block.size || 10, color: block.color || '#999999', lineHeight: block.lineHeight });
  const content = esc(block.text).replace(/\n/g, '<br />');
  return wrapBlock(`<div style="${style}">${content}</div>`, block, ctx);
}

const RENDERERS = {
  image: renderImage,
  text: renderText,
  contact: renderContact,
  social: renderSocial,
  button: renderButton,
  divider: renderDivider,
  spacer: renderSpacer,
  disclaimer: renderDisclaimer,
};

function renderColumn(col, ctx) {
  const blocks = (col.blocks || [])
    .map((b) => (RENDERERS[b.type] ? RENDERERS[b.type](b, ctx) : ''))
    .filter(Boolean)
    .join('');
  // cellpadding="0" on the row table already zeroes td padding.
  const padR = col.paddingRight || 0;
  const padL = col.paddingLeft || 0;
  const paddingStyle = (padR || padL) ? `padding:0 ${padR ? `${padR}px` : 0} 0 ${padL ? `${padL}px` : 0};` : '';
  const style = [
    `vertical-align:${col.valign || 'top'};`,
    col.align && col.align !== 'left' ? `text-align:${col.align};` : '',
    paddingStyle,
    col.width ? `width:${col.width}px;` : '',
    col.borderLeft ? `border-left:${col.borderLeft.thickness}px solid ${col.borderLeft.color};` : '',
  ].join('');
  const widthAttr = col.width ? ` width="${col.width}"` : '';
  return `<td${widthAttr} style="${style}">${blocks}</td>`;
}

function renderRow(row, ctx) {
  const cols = (row.columns || []).map((c) => renderColumn(c, ctx)).join('');
  if (!cols) return '';
  const rowTable = `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr>${cols}</tr></table>`;
  // cellpadding="0" on the outer table covers the spacing-less case.
  return row.spacingTop > 0
    ? `<tr><td style="padding:${row.spacingTop}px 0 0 0;">${rowTable}</td></tr>`
    : `<tr><td>${rowTable}</td></tr>`;
}

/**
 * Compile a signature spec to Gmail-safe HTML.
 * options.annotate adds data-sig-block attributes for the Studio preview —
 * NEVER use it for the copied/installed output.
 */
export function compileSignatureHtml(spec, options = {}) {
  if (!spec || !Array.isArray(spec.rows)) return '';
  const g = spec.global || {};
  const ctx = {
    font: fontStack(spec),
    fontSize: g.fontSize || 13,
    textColor: g.textColor || '#333333',
    linkColor: g.linkColor || '#1a6dad',
    accentColor: g.accentColor || '#0f4c81',
    maxWidth: g.maxWidth || 520,
    annotate: Boolean(options.annotate),
  };
  const rows = spec.rows.map((r) => renderRow(r, ctx)).filter(Boolean).join('');
  if (!rows) return '';
  return (
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" ` +
    `style="border-collapse:collapse;font-family:${ctx.font};">${rows}</table>`
  );
}

/** Plain-text rendition for the clipboard's text/plain flavor. */
export function compileSignatureText(spec) {
  const lines = [];
  for (const row of spec?.rows || []) {
    for (const col of row.columns || []) {
      for (const block of col.blocks || []) {
        if (block.type === 'text' || block.type === 'disclaimer') {
          const segs = Array.isArray(block.segments) ? block.segments.map((s) => s?.text || '').join('') : '';
          const text = segs.trim() || String(block.text || '').trim();
          if (text) lines.push(text);
        } else if (block.type === 'contact') {
          const labels = (block.items || []).map((i) => i.label).filter(Boolean);
          if (labels.length) lines.push(labels.join((block.separator || ' | ').trim() === '' ? ' ' : ` ${(block.separator || '|').trim()} `));
        } else if (block.type === 'social') {
          const labels = (block.icons || []).filter((i) => i.href).map((i) => `${i.label || i.network}: ${i.href}`);
          if (labels.length) lines.push(labels.join('  '));
        } else if (block.type === 'button') {
          if (block.href) lines.push(`${block.text}: ${block.href}`);
        }
      }
    }
  }
  return lines.join('\n');
}

/** Stats for the character meter + install guard. */
export function getSignatureStats(spec) {
  const html = compileSignatureHtml(spec);
  let imageCount = 0;
  for (const row of spec?.rows || []) {
    for (const col of row.columns || []) {
      for (const block of col.blocks || []) {
        if (block.type === 'image' && block.src) imageCount += 1;
        if (block.type === 'social') imageCount += (block.icons || []).filter((i) => i.iconUrl).length;
      }
    }
  }
  return {
    chars: html.length,
    limit: SIGNATURE_CHAR_LIMIT,
    pctOfLimit: Math.round((html.length / SIGNATURE_CHAR_LIMIT) * 100),
    overLimit: html.length > SIGNATURE_CHAR_LIMIT,
    nearLimit: html.length > SIGNATURE_CHAR_LIMIT * 0.8,
    imageCount,
  };
}
